import React from 'react';
import { Image, Select, Input } from 'antd';
import { searchableSelect } from '../../../../utils/selectSearch';
import { Camera, Search, SlidersHorizontal, CircleCheck, CircleX, ImageOff } from 'lucide-react';
import dayjs from 'dayjs';
import { StatusTag, EmptyState, CollapsibleCard } from '../../../common';
import { useCampaignImageGallery } from './campaignDetail.controller';

const { Option } = Select;

function CampaignImageGallery({ campaign, contracts }) {
    const g = useCampaignImageGallery({ campaign, contracts });

    return (
        <CollapsibleCard
            title="Galería de Imágenes"
            icon={<Camera />}
            iconTone="success"
            defaultExpanded={false}
            badges={<StatusTag tone="neutral">{g.allImages.length} imágenes</StatusTag>}>
            {/* Filters */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center mb-5">
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-surface-500">
                    <SlidersHorizontal className="text-[15px]" />
                    Filtros
                </span>
                <Select
                    value={g.filterStore}
                    onChange={g.setFilterStore}
                    className="w-full lg:w-64"
                    placeholder="Todos los pisos"
                    {...searchableSelect}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: 260 }}>
                    <Option value="all">Todos los pisos ({g.uniqueStores.length})</Option>
                    {g.uniqueStores.map((store) => (
                        <Option key={store.id} value={store.id}>
                            {store.name}
                        </Option>
                    ))}
                </Select>
                <Select value={g.filterMaterial} onChange={g.setFilterMaterial} className="w-full lg:w-56" placeholder="Todos los materiales">
                    <Option value="all">Todos los materiales ({g.uniqueMaterials.length})</Option>
                    {g.uniqueMaterials.map((material) => (
                        <Option key={material} value={material}>
                            {material}
                        </Option>
                    ))}
                </Select>
                <Input
                    placeholder="Buscar por piso, cliente o material..."
                    allowClear
                    value={g.searchTerm}
                    onChange={(e) => g.setSearchTerm(e.target.value)}
                    prefix={<Search className="text-surface-400" />}
                    className="w-full lg:max-w-[300px]"
                />
                {/* Results Count */}
                {g.filteredImages.length !== g.allImages.length && (
                    <span className="text-[13px] text-surface-500 lg:ml-auto tabular-nums">
                        Mostrando {g.filteredImages.length} de {g.allImages.length} imágenes
                    </span>
                )}
            </div>

            {/* Image Grid */}
            {g.filteredImages.length === 0 ? (
                <EmptyState icon={<ImageOff />} title="Sin imágenes" description="No se encontraron imágenes con los filtros seleccionados" />
            ) : (
                <Image.PreviewGroup>
                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {g.filteredImages.map((img) => (
                            <div key={img.key} className="min-w-0">
                                <div className="relative overflow-hidden rounded-lg border border-surface-200 bg-surface-50">
                                    <Image
                                        src={img.url}
                                        alt={`${img.storeName} - ${img.material}`}
                                        width="100%"
                                        height={140}
                                        className="object-cover"
                                        rootClassName="block"
                                    />

                                    {/* Material badge */}
                                    <div className="absolute top-2 left-2 pointer-events-none">
                                        <StatusTag tone={img.type === 'general' ? 'info' : 'violet'}>
                                            {img.material.length > 10 ? img.material.slice(0, 10) + '...' : img.material}
                                        </StatusTag>
                                    </div>

                                    {/* Availability badge */}
                                    <div className="absolute top-2 right-2 pointer-events-none">
                                        <StatusTag tone={img.available ? 'success' : 'danger'} icon={img.available ? <CircleCheck /> : <CircleX />}>
                                            {img.available ? 'Sí' : 'No'}
                                        </StatusTag>
                                    </div>
                                </div>

                                {/* Store name footer */}
                                <div className="mt-2 min-w-0">
                                    <div className="text-xs font-medium text-surface-800 truncate" title={img.storeName}>
                                        {img.storeName}
                                    </div>
                                    <div className="text-xs text-surface-500 tabular-nums" title={dayjs(img.date).format('DD/MM/YY')}>
                                        {dayjs(img.date).format('DD/MM/YY')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Image.PreviewGroup>
            )}
        </CollapsibleCard>
    );
}

export default CampaignImageGallery;
